import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";

import { setSection, updateSection } from "../../../../assignmentSlice";

import { getContentHeight } from "../../../../../../utils/textUtils";
import { getStartingIndex } from "../../../../../../utils/resumeUtils";
import { processGroupData } from "../../../../../../utils/parseUtils";
import { 
  evaluateIntermediateSyntax
} from "../../../../../../utils/syntaxEvaluation";
import { 
  updateIntermediatePageWithCorrectness
} from "../../../../../../utils/syntaxDataHelpers";

import { phraseInfo, tagInfo } from "../constants";

import ArrowButton from "../../../widgets/ArrowButton";
import CorrectOverlay from "../../../widgets/CorrectOverlay";
import Toast from "../../../widgets/Toast";
import TagButtonList from "../TagButtonList";
import GroupElement from "../GroupElement";
import TokenElement from "../TokenElement";

const IntermediatePage = ({ section, setCurrentStep, isPreview, isReview }) => {
  const dispatch = useDispatch();
  const [pageList, setPageList] = useState([]);

  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedGroupIndex, setSelectedGroupIndex] = useState(-1);
  const [overlayVisible, setOverlayVisible] = useState(false);
  const [overlayType, setOverlayType] = useState('correct'); // 'correct' or 'incorrect'
  const [elementCorrectness, setElementCorrectness] = useState({});
  const [toastVisible, setToastVisible] = useState(false);
  const [toastMessage, setToastMessage] = useState("");

  const lineHeight = "50px";
  const currentPage = pageList[currentIndex] || {
    type: "",
    data: [],
    tags: [],
  };

  useEffect(() => {
    if (pageList.length > 0) return;

    if (!isPreview && section.answer.length > 0) {
      setPageList(section.answer);
      setCurrentIndex(getStartingIndex(section.resumeIndex));
      return;
    }

    if (section.question) {
      const allTagList = Object.keys(tagInfo);
      const allPhraseList = Object.keys(phraseInfo);

      let tempPageList = [];
      section.question.data.forEach((element) => {
        const tokens = element.tokens;
        const tokenGroups = processGroupData(
          element.parsed_syntax.slice()
        ).sort((a, b) =>
          a.token_indices.at(0) - b.token_indices.at(0) === 0
            ? b.token_indices.at(-1) - a.token_indices.at(-1)
            : a.token_indices.at(0) - b.token_indices.at(0)
        );
        const answerData = tokenGroups
          .filter((group) => allTagList.includes(group.type))
          .map((group) => {
            return {
              type: group.type,
              token_indices: group.token_indices,
              isSubmitted: false,
              isCorrect: null, // Initialize as null, will be set on submission
              studentAnswer: null,
              elementId: null
            };
          });
        const phraseData = {};
        const tagSet = new Set();
        allTagList
          .filter((groupType) =>
            ["s", "v", "o", "io", "do", "sc", "oc"].includes(groupType)
          )
          .forEach((groupType) => {
            tagSet.add(groupType);
          });

        tokenGroups.forEach((group) => {
          const groupType = group.type;
          if (allTagList.includes(groupType)) {
            tagSet.add(groupType);
          } else if (allPhraseList.includes(groupType)) {
            const startIndex = group.token_indices.at(0);
            const endIndex = group.token_indices.at(-1);
            if (phraseData[startIndex]) {
              phraseData[startIndex].start.push(phraseInfo[groupType]);
            } else {
              phraseData[startIndex] = {
                start: [phraseInfo[groupType]],
                end: [],
              };
            }
            if (phraseData[endIndex]) {
              phraseData[endIndex].end.unshift(phraseInfo[groupType]);
            } else {
              phraseData[endIndex] = {
                start: [],
                end: [phraseInfo[groupType]],
              };
            }
          }
        });

        const blockList = [];

        tokens.forEach((token, tokenIndex) => {
          {
            blockList.push({
              type: "token",
              text: token.text,
              index: tokenIndex,
              space_right: token.space_right,
              phraseStart: phraseData[tokenIndex]
                ? phraseData[tokenIndex].start
                : [],
              phraseEnd: phraseData[tokenIndex]
                ? phraseData[tokenIndex].end
                : [],
            });
          }
        });

        tempPageList.push({
          type: "syntax",
          isSubmitted: false,
          submitCount: 0,
          data: blockList,
          tags: Array.from(tagSet),
          answers: answerData.map(answer => ({
            ...answer,
            firstAttemptCorrect: null, // Track first attempt result for PDF
            hasBeenAttempted: false // Track if this element has been attempted
          })),
          correctnessTracking: {
            totalElements: answerData.length,
            correctElements: 0,
            incorrectElements: 0,
            lastSubmitTime: null,
            level: 'intermediate'
          }
        });
      });
      setPageList(tempPageList);
    }
  }, [section]);

  const handleClickToken = (element, elementIndex) => {
    if (selectedGroupIndex === -1) {
      const groupIndex = element.index;
      const newPageList = pageList.map((page, pageIndex) => {
        if (pageIndex === currentIndex) {
          const pageData = page.data.slice();
          const newGroup = {
            type: "group",
            groupIndex: groupIndex,
            groupType: "",
            isSubmitted: false,
            text: [
              {
                type: element.type,
                text: element.text,
                index: element.index,
                space_right: element.space_right,
                phraseStart: element.phraseStart,
                phraseEnd: element.phraseEnd,
              },
            ],
          };
          pageData.splice(elementIndex, 1, newGroup);
          return {
            ...page,
            data: pageData,
          };
        }
        return page;
      });
      setPageList(newPageList);
      dispatch(setSection({ ...section, answer: newPageList }));
      setSelectedGroupIndex(groupIndex);
    } else {
      const groupElementIndex = pageList[currentIndex].data.findIndex(
        (block) => block.groupIndex === selectedGroupIndex
      );
      const selectedGroup = pageList[currentIndex].data.find(
        (block) => block.groupIndex === selectedGroupIndex
      );

      if (
        selectedGroup.groupType === "" &&
        elementIndex > groupElementIndex &&
        selectedGroup.text.length === 1 &&
        currentPage.data
          .slice(groupElementIndex + 1, elementIndex + 1)
          .every((block) => block.type === "token")
      ) {
        const newPageList = pageList.map((page, pageIndex) => {
          if (pageIndex === currentIndex) {
            const pageData = page.data.slice();
            const group = pageData.find(
              (block) => block.groupIndex === selectedGroupIndex
            );
            const newGroup = {
              ...group,
              text: [
                ...group.text,
                ...pageData.slice(groupElementIndex + 1, elementIndex + 1),
              ],
            };
            pageData.splice(
              groupElementIndex,
              elementIndex - groupElementIndex + 1,
              newGroup
            );
            return {
              ...page,
              data: pageData,
            };
          }
          return page;
        });
        setPageList(newPageList);
        dispatch(setSection({ ...section, answer: newPageList }));
      }
    }
  };

  const handleClickGroup = (element, elementIndex) => {
    if (element.isSubmitted) {
      alert("정답으로 제출된 성분입니다.");
      return;
    }
    if (selectedGroupIndex === element.groupIndex) {
      const newPageList = pageList.map((page, pageIndex) => {
        if (pageIndex === currentIndex) {
          const pageData = page.data.slice();
          pageData.splice(elementIndex, 1, ...element.text);
          return {
            ...page,
            data: pageData,
          };
        }
        return page;
      });
      setPageList(newPageList);
      dispatch(setSection({ ...section, answer: newPageList }));
      setSelectedGroupIndex(-1);
    } else {
      setSelectedGroupIndex(element.groupIndex);
    }
  };

  const handleClickTag = (tag) => {
    if (selectedGroupIndex === -1) {
      return;
    }
    const newPageList = pageList.map((page, pageIndex) => {
      if (pageIndex === currentIndex) {
        return {
          ...page,
          data: page.data.map((block) => {
            if (
              block.type === "group" &&
              block.groupIndex === selectedGroupIndex
            ) {
              return {
                ...block,
                groupType: tag,
              };
            }
            return block;
          }),
        };
      }
      return page;
    });
    setPageList(newPageList);
    dispatch(setSection({ ...section, answer: newPageList }));
    setSelectedGroupIndex(-1);
  };

  const handlePrev = () => {
    setCurrentIndex(currentIndex - 1);
    setSelectedGroupIndex(-1);
  };

  const handleNext = () => {
    if (currentIndex < pageList.length - 1) {
      setCurrentIndex(currentIndex + 1);
      setSelectedGroupIndex(-1);
    } else {
      let isAllSubmitted = true;
      pageList.forEach((page) => {
        if (page.isSubmitted === false) {
          isAllSubmitted = false;
          return;
        }
      });
      if (isAllSubmitted) {
        if (!isPreview && !isReview) {
          dispatch(
            updateSection({
              sectionId: section.id,
              data: { is_finished: true, answer: pageList },
            })
          );
        }
        alert("학습을 완료하셨습니다.");
        setCurrentStep(2);
      } else {
        alert("문제를 덜 풀었습니다.");
      }
    }
  };

  const handleSubmit = () => {
    const submitCount = currentPage.submitCount + 1;

    let evaluationResult;
    let newCurrentPage;

    // Check if max attempts (3) reached
    if (submitCount >= 3) {
      // Auto-reveal all correct answers after 3 attempts
      const allTagList = Object.keys(tagInfo);
      const correctAnswers = currentPage.answers.filter(answer =>
        allTagList.includes(answer.type)
      );

      // Mark all groups as correct with auto-reveal
      const autoRevealedData = currentPage.data.map((block) => {
        if (block.type === "token") {
          // Check if this token should be part of a group
          const matchingAnswer = correctAnswers.find(answer =>
            answer.token_indices.includes(block.index)
          );

          if (matchingAnswer) {
            // Convert token to group if it's part of an answer
            const groupTokens = correctAnswers
              .filter(ans =>
                ans.token_indices.length === matchingAnswer.token_indices.length &&
                ans.token_indices.every(idx => matchingAnswer.token_indices.includes(idx))
              )[0];

            // Check if this is the first token in the group
            if (groupTokens && block.index === Math.min(...groupTokens.token_indices)) {
              return {
                type: "group",
                groupType: groupTokens.type,
                isSubmitted: true,
                isCorrect: true,
                autoRevealed: true,
                text: groupTokens.token_indices.map(idx => {
                  const tokenData = currentPage.data.find(d => d.type === "token" && d.index === idx);
                  return tokenData || { text: "", index: idx, space_right: true };
                })
              };
            }
          }
          return block;
        } else if (block.type === "group") {
          const tokenIndices = block.text.map(t => t.index);
          const matchingAnswer = correctAnswers.find(answer => {
            const answerIndices = [...answer.token_indices].sort((a, b) => a - b);
            const blockIndices = [...tokenIndices].sort((a, b) => a - b);
            return answerIndices.length === blockIndices.length &&
                   answerIndices.every((idx, i) => idx === blockIndices[i]);
          });

          if (matchingAnswer) {
            return {
              ...block,
              groupType: matchingAnswer.type,
              isSubmitted: true,
              isCorrect: true,
              autoRevealed: true
            };
          }
        }
        return block;
      });

      // Filter out tokens that are now part of groups
      const filteredData = [];
      const processedIndices = new Set();

      autoRevealedData.forEach(block => {
        if (block.type === "group" && block.autoRevealed) {
          block.text.forEach(t => processedIndices.add(t.index));
          filteredData.push(block);
        } else if (block.type === "token" && !processedIndices.has(block.index)) {
          filteredData.push(block);
        } else if (block.type === "group" && !block.autoRevealed) {
          filteredData.push(block);
        }
      });

      // Mark all answers as submitted
      const autoRevealedAnswers = currentPage.answers.map(answer => ({
        ...answer,
        isSubmitted: true,
        isCorrect: true,
        studentAnswer: answer.type,
        autoRevealed: true
      }));

      newCurrentPage = {
        ...currentPage,
        data: filteredData,
        answers: autoRevealedAnswers,
        submitCount: submitCount,
        isSubmitted: true,
        autoRevealed: true
      };

      setOverlayType('incorrect'); // Show X overlay for max attempts
    } else {
      // Normal evaluation (attempts 1-2)
      evaluationResult = evaluateIntermediateSyntax(
        currentPage.data,
        currentPage.answers,
        currentIndex
      );

      // Update page with correctness information
      newCurrentPage = updateIntermediatePageWithCorrectness(currentPage, evaluationResult);
      newCurrentPage.submitCount = submitCount;
      newCurrentPage.isSubmitted = evaluationResult.isAllCorrect;

      // Store correctness for overlay display
      setElementCorrectness(evaluationResult.correctness);
      setOverlayType(evaluationResult.isAllCorrect ? 'correct' : 'incorrect');
    }

    // Update page list
    const newPageList = pageList.map((page, pageIndex) => {
      if (pageIndex === currentIndex) {
        return newCurrentPage;
      }
      return page;
    });

    setPageList(newPageList);
    dispatch(setSection({ ...section, answer: newPageList }));
    dispatch(
      updateSection({ sectionId: section.id, data: { answer: newPageList } })
    );

    // Show overlay feedback or toast
    if (submitCount < 3) {
      setOverlayVisible(true);
      setTimeout(() => {
        setOverlayVisible(false);

        // Auto-advance if all correct
        if (newCurrentPage.isSubmitted) {
          setTimeout(() => {
            if (currentIndex < pageList.length - 1) {
              setCurrentIndex(currentIndex + 1);
              setSelectedGroupIndex(-1);
            }
          }, 200);
        }
      }, 600);
    } else {
      // Max attempts reached - show X overlay and toast (user must manually navigate)
      setOverlayVisible(true);
      setToastMessage("제출 횟수를 초과하였습니다.\n정답을 확인하고, 다음 문제로 넘어가세요.");
      setToastVisible(true);
      setTimeout(() => {
        setOverlayVisible(false);
      }, 600);
      setTimeout(() => {
        setToastVisible(false);
      }, 3000);
    }

    setSelectedGroupIndex(-1);
  };

  return (
    <>
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          paddingRight: "20px",
          justifyContent: "space-between",
          height: getContentHeight(),
          fontFamily: '"Noto Sans KR", sans-serif',
          backgroundColor: "#FFFFFF",
        }}
      >
        <ArrowButton
          direction="left"
          onClick={() => handlePrev()}
          visibleCondition={currentIndex > 0}
        />
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            width: "90%",
            height: "100%",
            gap: "20px",
          }}
        >
          <div
            style={{
              fontSize: "20px",
              marginBottom: "20px",
            }}
          >
            아래 표시된 단어 (구, 절)의 문장 형식을 찾아 선택하시오.
          </div>
          <div
            style={{
              fontSize: "28px",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              gap: "50px",
            }}
          >
            {currentPage.type === "syntax" && (
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  whiteSpace: "pre-wrap",
                }}
              >
                {currentPage.data.map((element, elementIndex) => {
                  return (
                    <>
                      {element.type === "token" && (
                        <TokenElement
                          element={element}
                          lineHeight={lineHeight}
                          handleClickToken={() =>
                            handleClickToken(element, elementIndex)
                          }
                        />
                      )}
                      {element.type === "group" && (
                        <GroupElement
                          element={element}
                          lineHeight={lineHeight}
                          selectedGroupIndex={selectedGroupIndex}
                          handleClickGroup={() =>
                            handleClickGroup(element, elementIndex)
                          }
                        />
                      )}
                    </>
                  );
                })}
              </div>
            )}
          </div>
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "center",
              gap: "30px",
            }}
          >
            <TagButtonList
              tagList={currentPage.tags}
              onClick={handleClickTag}
            />
          </div>
        </div>
        <ArrowButton
          direction="right"
          onClick={() => handleNext()}
          visibleCondition={true}
        />
        <button
          style={{
            position: "absolute", // 부모(80% width) 안에서 절대 위치
            bottom: "90px", // 최하단으로부터 10px 떨어짐
            right: "60px", // 우측으로부터 10px 떨어짐
            padding: "10px 35px",
            backgroundColor: currentPage.isSubmitted ? "#EBEBEB" : "#2D2D2D",
            color: currentPage.isSubmitted === true ? "#737373" : "white",
            border: "none",
            fontSize: "18px",
            borderRadius: "5px",
            cursor: "pointer",
          }}
          onClick={() => handleSubmit()}
        >
          제출하기
        </button>
      </div>
      <div
        style={{
          opacity: overlayVisible ? 1 : 0, // Fade-in/out
          visibility: overlayVisible ? "visible" : "hidden", // DOM에서 숨김
          transition: "opacity 200ms ease-out, visibility 200ms",
        }}
      >
        <CorrectOverlay bool={overlayType === 'correct'} />
      </div>
      <div
        style={{
          position: "fixed",
          bottom: 0,
          left: 0,
          height: "70px",
          width: "100vw",
          backgroundColor: "#F6F8FA", // 바 색상
          color: "#737373",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        {currentIndex + 1} / {pageList.length}
      </div>
      <Toast message={toastMessage} visible={toastVisible} />
    </>
  );
};

export default IntermediatePage;
